/**
 * O `AppPalette` do Flutter, aplicado às variáveis do CSS.
 *
 * Um tema aqui não é uma segunda paleta escrita à mão: é a mesma Dusk Petal
 * girada no círculo de cores. Girar em vez de repintar mantém o contraste que
 * já foi conferido — o texto continua legível sobre o fundo porque só o matiz
 * muda, o brilho não.
 *
 * O que não gira: o texto (pétala), o âmbar de aviso, o verde de presença e os
 * acentos do Cinema e do anime. Esses carregam significado próprio (atenção,
 * "assistido", "de quem é"), e girar junto trocaria o significado pela
 * decoração — é a mesma lista que o `app_colors.dart` deixa de fora do `_t`.
 *
 * Mora no site e não em `shared` porque escreve em `document`: o celular em
 * React Native não tem folha de estilo para reescrever.
 */

import { PALETAS, paletaPorId, type Paleta } from '@nosso-sempre/shared';

/** A mesma chave do `SharedPreferences` do Flutter. */
const CHAVE = 'tema:escolhido';

// ── A conta de girar, igual à do `AppPalette.aplicar` ────────────────

interface HSL {
  h: number;
  s: number;
  l: number;
}

function paraHsl(r: number, g: number, b: number): HSL {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return { h: h < 0 ? h + 360 : h, s, l };
}

function deHsl({ h, s, l }: HSL): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [r + m, g + m, b + m];
}

const doisDigitos = (n: number): string =>
  Math.round(Math.min(1, Math.max(0, n)) * 255)
    .toString(16)
    .padStart(2, '0');

/**
 * Gira um `#rrggbb`. Cinza não tem matiz para girar: girar mesmo assim
 * inventaria uma cor onde o desenho pediu neutro — por isso o corte em 0.02,
 * o mesmo do Flutter.
 */
export function girar(hex: string, paleta: Paleta): string {
  if (paleta.giro === 0 && paleta.saturacao === 1) return hex;
  const n = parseInt(hex.slice(1), 16);
  const hsl = paraHsl(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
  if (hsl.s < 0.02) return hex;
  const [r, g, b] = deHsl({
    h: (hsl.h + paleta.giro) % 360,
    s: Math.min(1, Math.max(0, hsl.s * paleta.saturacao)),
    l: hsl.l,
  });
  return `#${doisDigitos(r)}${doisDigitos(g)}${doisDigitos(b)}`;
}

/** O mesmo, mas devolvendo `rgba()` — para as cores que já vêm com alfa. */
function girarComAlfa(hex: string, alfa: number, paleta: Paleta): string {
  const girado = girar(hex, paleta);
  const n = parseInt(girado.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alfa})`;
}

// ── O que gira ───────────────────────────────────────────────────────

/**
 * Variável do CSS -> a cor original de `app_colors.dart`.
 *
 * A lista é exatamente a dos getters que passam pelo `_t` lá. O que não está
 * aqui (pétala, âmbar, sucesso, cinema, anime) fica parado de propósito.
 */
const BASES: ReadonlyArray<readonly [string, string]> = [
  ['--crepusculo', '#1a1a2e'], // twilight
  ['--veludo', '#2d1b33'], // velvet
  ['--rosa-quartzo', '#f4c2c2'], // roseQuartz
  ['--rosa', '#c2185b'], // deepRose
  ['--ouro', '#e8d5b7'], // blushGold
  ['--lavanda', '#d4b5d6'], // softLavender
  ['--luar', '#f0e6ff'], // moonlight
  ['--aurora-rosa', '#ff6f91'],
  ['--aurora-ouro', '#f5c97b'],
  ['--lavanda-viva', '#b79ced'], // auroraLilac
  ['--aurora-agua', '#7ee8d2'],
  ['--aurora-azul', '#4a90d9'],
  ['--tinta', '#100a1c'], // inkDeep
  ['--ameixa', '#3a2352'], // plum
  ['--seda', '#2e203e'], // silk
  ['--preto-fundo', '#12091a'], // deepBlack
  ['--apagado', '#8a7a92'], // mutedPurple
  ['--erro', '#e5739b'],
  ['--cintila-base', '#1c1228'], // shimmerBase
  ['--cintila-alto', '#2a1f3a'], // shimmerHighlight
];

/**
 * As derivadas: no Flutter são `.withValues(alpha:)` sobre uma cor que gira,
 * então giram junto. As que saem da pétala (texto alto, médio, desligado) não
 * estão aqui porque a pétala não gira.
 */
const COM_ALFA: ReadonlyArray<readonly [string, string, number]> = [
  ['--vidro', '#f0e6ff', 0.12], // surfaceGlass
  ['--vidro-suave', '#f0e6ff', 0.08], // glassSoft
  ['--borda', '#f0e6ff', 0.18],
  ['--divisor', '#f0e6ff', 0.18],
  ['--painel', '#100a1c', 0.55], // panelGlass
  ['--texto-fraco', '#f4c2c2', 0.72], // roseQuartz 72%
  ['--brilho-rosa', '#c2185b', 0.35],
  ['--brilho-ouro', '#e8d5b7', 0.3],
  ['--brilho-lavanda', '#d4b5d6', 0.28],
];

/**
 * O esquema Material que o `ColorScheme.fromSeed(deepRose)` monta.
 *
 * Lá ele é recalculado a cada troca de tema, porque a semente é o `deepRose`
 * já girado. Aqui os valores foram medidos no tema rosa e giram pelo mesmo
 * ângulo — não é a mesma conta (o Material trabalha em HCT, não em HSL), mas
 * erra por pouco e é o que impede um botão do Material de ficar rosa numa casa
 * que escolheu azul.
 */
const MATERIAL: ReadonlyArray<readonly [string, string]> = [
  // onPrimary. Não é o `#551d2d` do Flutter: aquele dá 2,23:1 contra o
  // `--rosa` e reprova na WCAG AA. Ver a nota em `estilos/globais.css`.
  ['--m3-na-primaria', '#fff5f5'],
  ['--m3-no-fundo', '#efdee0'], // onSurface
  ['--m3-no-fundo-2', '#d6c2c4'], // onSurfaceVariant
  ['--m3-contorno', '#9e8c8f'], // outline
  ['--m3-contorno-2', '#524346'], // outlineVariant
  ['--m3-caixa', '#3c3234'], // surfaceContainerHighest
  ['--m3-2-caixa', '#5b3f45'], // secondaryContainer
  ['--m3-no-2-caixa', '#ffd9df'], // onSecondaryContainer
];

/** Os degradês do `app_theme.dart`: as paradas são cores que giram. */
const DEGRADES: ReadonlyArray<readonly [string, number, readonly string[]]> = [
  ['--festivo', 135, ['#1a1a2e', '#2d1b33', '#c2185b']],
  ['--rosa-ouro', 135, ['#ff6f91', '#c2185b', '#e8d5b7']],
  ['--lavada', 135, ['#d4b5d6', '#f0e6ff']],
];

// ── Aplicar ──────────────────────────────────────────────────────────

let ativa: Paleta = PALETAS[0]!;

/** A paleta em uso agora, para quem precisa da cor da amostra. */
export const paletaAtiva = (): Paleta => ativa;

/**
 * Pinta a paleta escolhida. Reescrever as variáveis de `:root` troca o app
 * inteiro num quadro, sem remontar árvore nenhuma — é o que o
 * `ValueListenableBuilder` do `main.dart` faz lá, de graça.
 */
export function aplicarPaleta(id: string | null | undefined): void {
  const paleta = paletaPorId(id ?? undefined);
  ativa = paleta;
  const raiz = document.documentElement.style;

  for (const [nome, cor] of BASES) raiz.setProperty(nome, girar(cor, paleta));
  for (const [nome, cor, alfa] of COM_ALFA) raiz.setProperty(nome, girarComAlfa(cor, alfa, paleta));
  for (const [nome, cor] of MATERIAL) raiz.setProperty(nome, girar(cor, paleta));
  for (const [nome, angulo, paradas] of DEGRADES) {
    const giradas = paradas.map((c) => girar(c, paleta)).join(', ');
    raiz.setProperty(nome, `linear-gradient(${angulo}deg, ${giradas})`);
  }

  try {
    localStorage.setItem(CHAVE, paleta.id);
  } catch {
    // Falhar em lembrar a cor não pode derrubar a troca de tema.
  }
}

/**
 * Lê o tema salvo antes do primeiro quadro.
 *
 * Sem isto o app sempre abria no rosa e só trocava quando os ajustes chegavam
 * do servidor — e um app que pisca de rosa para azul a cada abertura parece
 * quebrado, mesmo acertando no fim.
 */
export function restaurarPaleta(): void {
  try {
    const id = localStorage.getItem(CHAVE);
    if (id) aplicarPaleta(id);
  } catch {
    // Sem armazenamento local o app abre no rosa. Não é motivo para travar.
  }
}
